import React, { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useHistory } from 'react-router-dom';
import {
  AdditionalInformation,
  Origin,
  ReferenceBrandType,
  ReferenceSource,
} from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/IAMEquivalentReferences';
/* eslint-disable max-len */

import { ROUTER_CART } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { AddToEstimateButton } from 'components/AddToEstimate';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import { ReferencePriceSection } from 'components/ReferencePriceSection';
import StockDisplay from 'components/StockInfo';
import { addReferenceByRefNumberRequest } from 'domains/basket/Basket.store';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { IAM, STANDARD } from 'domains/catalog/Catalog.types';
import { getIAMReference, getIAMReferences, getPrice, ReferencePriceType } from 'domains/references';
import { getDealerType, getSparePartsView } from 'domains/user';
import {
  CellWrapper,
  EmptyPicture,
  HeaderWrapper,
  OptionWrapper,
  TableWrapper,
} from 'pages/CatalogPage/IAM/FullTextSearchResult/CompareTable.styled';
import { getDisplayName, getTranslatedDisplayName } from 'pages/CatalogPage/IAM/FullTextSearchResult/utils';
import { getFirstImageUrl } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferenceCard/IAMPlateReferenceCard';
import { handleAddToEstimate } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/IAMReferenceCard';
import { PictureSection } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/PictureSection';
import { theme } from 'styles';
import {
  ErrorAlert,
  Flex,
  FlexAlignItemsType,
  FlexJustifyType,
  GreyButton,
  MarginBox,
  NotificationLink,
  notifyTop,
  Select,
  SelectOptionSingle,
  Text,
} from 'UI';
import { BreakpointSelectable, getData, hasPrice } from 'utils';
import { TRACKING_EVENT_GO_TO_CART_SHORTCUT } from 'utils/eventTracker/EventTracker.types';

export interface CompareTableState {
  firstReference: string;
  secondReference: string;
  thirdReference: string;
}

export interface RowProps extends TableCellRowPositionProps {
  selectedReferences?: string[];
  vehicleKey?: string;
}

export const DESCRIPTION_CELL_WIDTH = 250;
export const VALUE_CELL_WIDTH = 300;

export const CompareTableHeader = () => {
  const { t } = useTranslation();
  return (
    <HeaderWrapper>
      <Text type="heading3">{t('catalog.search.iam.compare_table.title', 'Comparative')}</Text>
      <Text type="h5_light">
        {t('catalog.search.iam.compare_table.description', 'Select the reference you want to compare')}
      </Text>
    </HeaderWrapper>
  );
};

type TableCellRowPositionProps = {
  rowIndex?: number;
};

type TableCellColumnPositionProps = {
  columnIndex?: number;
};

type TableCellPositionProps = TableCellRowPositionProps & TableCellColumnPositionProps;

type TableCellProps = {
  children: ReactNode;
  align?: BreakpointSelectable<FlexAlignItemsType>;
  justify?: BreakpointSelectable<FlexJustifyType>;
  minWidth?: BreakpointSelectable<string | number>;
  maxWidth?: BreakpointSelectable<string | number>;
} & TableCellPositionProps;

const TableCell = ({
  children,
  rowIndex,
  columnIndex,
  align = 'center',
  justify = 'center',
  minWidth,
  maxWidth,
}: TableCellProps) => {
  return (
    <CellWrapper
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      align={align}
      justify={justify}
      minWidth={minWidth}
      maxWidth={maxWidth}
    >
      <MarginBox mx={16} my={12}>
        {children}
      </MarginBox>
    </CellWrapper>
  );
};

export const DescriptionCell = ({ description, rowIndex }: { description?: string } & TableCellRowPositionProps) => (
  <TableCell
    justify={'flex-start'}
    minWidth={DESCRIPTION_CELL_WIDTH}
    maxWidth={DESCRIPTION_CELL_WIDTH}
    rowIndex={rowIndex}
    columnIndex={0}
  >
    {description && (
      <Text type="base" bold>
        {description}
      </Text>
    )}
  </TableCell>
);

export const EmptyCell = ({ rowIndex, columnIndex }: TableCellPositionProps) => {
  return (
    <TableCell rowIndex={rowIndex} columnIndex={columnIndex} minWidth={VALUE_CELL_WIDTH}>
      <Text type="base" color="grey35">
        {'-'}
      </Text>
    </TableCell>
  );
};

interface ReferencesForSelect {
  referenceNumber: string;
  translatedDisplayName: string;
}

interface SummaryRowProps extends RowProps {
  compareReferencesList?: string[];
  handleSelect?: (referenceNumber: string, columnIndex?: number) => void;
}

const SummaryRow = ({
  selectedReferences,
  vehicleKey,
  compareReferencesList,
  handleSelect,
  rowIndex,
}: SummaryRowProps) => {
  const { t } = useTranslation();

  const compareReferences = useSelector((state: RootState) =>
    getIAMReferences(state, { vehicleKey, referenceNumbers: compareReferencesList }),
  );

  const referencesForSelect: ReferencesForSelect[] = compareReferences.map((reference) => {
    return {
      referenceNumber: reference.referenceNumber,
      translatedDisplayName: getTranslatedDisplayName(
        t,
        reference.referenceNumber,
        reference.name,
        reference.designation,
        reference.supplierReferences,
        reference.supplier,
      ),
    };
  });
  return (
    <Flex direction={'row'}>
      <DescriptionCell rowIndex={rowIndex} />
      {selectedReferences?.map((reference, index) => {
        return (
          <SummaryCell
            key={`${index}_${reference}`}
            vehicleKey={vehicleKey}
            referenceNumber={reference}
            referencesForSelect={referencesForSelect}
            handleSelect={handleSelect}
            rowIndex={rowIndex}
            columnIndex={index + 1}
          />
        );
      })}
    </Flex>
  );
};

interface EstimateSectionProps {
  referenceNumber: string;
  displayName: string;
  sparePartsView: string;
  origin?: Origin;
  supplierCode?: string;
  referenceSource?: ReferenceSource;
  price?: ReferencePriceType;
  isOEM?: boolean;
  isMaintenance?: boolean;
  availableForOrder?: boolean;
  brand?: ReferenceBrandType;
}

export const EstimateSection = ({
  referenceNumber,
  displayName,
  brand,
  sparePartsView,
  origin,
  supplierCode,
  referenceSource,
  price,
  isOEM,
  isMaintenance,
  availableForOrder,
}: EstimateSectionProps) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const handleAddToEstimateClick = (quantity?: number) => {
    handleAddToEstimate(dispatch, history, {
      referenceNumber,
      displayName,
      price,
      brand,
      origin,
      supplierCode,
      referenceSource,
      quantity,
    });
  };

  return hasPrice(sparePartsView, price) && !isOEM && !isMaintenance ? (
    <AddToEstimateButtonAndDialog
      handleAddToEstimateClick={handleAddToEstimateClick}
      displayed={availableForOrder}
      size={'lg'}
      isVehicleCatalog
    />
  ) : null;
};

interface SummaryCellProps extends TableCellPositionProps {
  referenceNumber?: string;
  vehicleKey?: string;
  referencesForSelect?: ReferencesForSelect[];
  handleSelect?: (referenceNumber: string, columnIndex?: number) => void;
}

const SummaryCell = ({
  referenceNumber,
  vehicleKey,
  referencesForSelect,
  rowIndex,
  columnIndex,
  handleSelect,
}: SummaryCellProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const reference = getData(useSelector((state: RootState) => getIAMReference(state, { vehicleKey, referenceNumber })));
  const imageUrl = getFirstImageUrl(reference);
  const sparePartsView = useSelector(getSparePartsView);
  const isR1 = useSelector(getDealerType) === 'R1';
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const availableForOrder = vehicleDetail?.vehicleBrand === 'ALPINE' ? isR1 : true;
  const price = getData(useSelector((state: RootState) => getPrice(state, referenceNumber)));

  const handleAddToCartClick = () => {
    if (referenceNumber) {
      notifyTop(
        'success',
        <Trans i18nKey={'catalog.universal_products.reference_card.added_to_basket.description'}>
          {'Universal product has been added to your cart'}
        </Trans>,
        undefined,
        <NotificationLink
          onClick={() => {
            trackAppEvent(TRACKING_EVENT_GO_TO_CART_SHORTCUT);
            history.push(ROUTER_CART);
          }}
        >
          <Trans i18nKey={'catalog.reference_card.added_to_basket.go_to_cart'}>{'Go to cart'}</Trans>
        </NotificationLink>,
      );
      dispatch(
        addReferenceByRefNumberRequest({
          reference: referenceNumber,
        }),
      );
    }
  };
  const mainReferenceName =
    columnIndex === 1
      ? reference &&
        getTranslatedDisplayName(
          t,
          reference.referenceNumber,
          reference.name,
          reference.designation,
          reference.supplierReferences,
          reference.supplier,
        )
      : undefined;

  const options = referencesForSelect?.map(
    (refForSelect) =>
      ({
        title: refForSelect.translatedDisplayName,
        value: refForSelect.referenceNumber,
      } as SelectOptionSingle),
  );

  return (
    <TableCell minWidth={VALUE_CELL_WIDTH} rowIndex={rowIndex} columnIndex={columnIndex}>
      <Flex minHeight={300} direction={'column'} justify={'center'} align={'center'} gap={20}>
        <Flex minHeight={150} maxHeight={150} align={'center'} justify={'center'}>
          {!referenceNumber ? (
            <EmptyPicture width={135} height={120} />
          ) : (
            <PictureSection
              documents={reference?.documents}
              brandImage={reference?.brandImageUrl}
              referenceImage={imageUrl}
              styles={{ imageBackground: theme.color.white }}
            />
          )}
        </Flex>
        <Flex align={'center'} justify={'center'} minWidth={300} minHeight={40} maxHeight={40}>
          {mainReferenceName ? (
            <Text type="base" bold>
              {mainReferenceName}
            </Text>
          ) : (
            <Select
              notFoundContent={<ErrorAlert message={t('common.alert.no_matches_found', 'No matches found')} />}
              onChange={(refNumber: string) => {
                handleSelect?.(refNumber, columnIndex);
              }}
              value={referenceNumber === '' ? undefined : referenceNumber}
              options={options}
              optionWrapper={OptionWrapper}
              placeholder={t('catalog.iam.compare.select.placeholder', 'Select another equivalent product')}
              bordered
              search
              alignLeft
              size={'large'}
            />
          )}
        </Flex>

        {!reference ? (
          <Flex direction={'row'} minHeight={50} maxHeight={50} gap={12} align={'center'} justify={'center'}>
            <AddToEstimateButton
              handleAddToEstimateClick={() => {
                return;
              }}
              size={'lg'}
              disabled
            />
            <GreyButton
              size={'large'}
              onClick={() => {
                return;
              }}
              stretch
              shape={'round'}
              disabled
            >
              <Flex minWidth={160} align={'center'} justify={'center'} gap={5}>
                {t('cart.action.add_to_cart', 'Add to cart')}
              </Flex>
            </GreyButton>
          </Flex>
        ) : (
          <Flex direction={'row'} minHeight={55} maxHeight={80} gap={12} align={'center'} justify={'center'}>
            <ReferencePriceSection
              referenceNumber={reference.referenceNumber}
              handleAddToCartClick={handleAddToCartClick}
              catalogSource={IAM}
              referenceType={STANDARD}
              hidePrice
              buttonSize={'large'}
              buttonShape={'round'}
              availableForOrder={availableForOrder}
              externalButton={
                <EstimateSection
                  referenceNumber={reference.referenceNumber}
                  displayName={getDisplayName(
                    reference.name,
                    reference.designation,
                    reference.supplierReferences,
                    reference.supplier,
                  )}
                  sparePartsView={sparePartsView}
                  origin={reference.origin}
                  supplierCode={reference.supplierCode}
                  referenceSource={reference.referenceSource}
                  price={price}
                  availableForOrder={availableForOrder}
                  brand={reference.brand}
                />
              }
            />
          </Flex>
        )}

        <Flex />
      </Flex>
    </TableCell>
  );
};

const StocksRow = ({ selectedReferences, vehicleKey, rowIndex }: RowProps) => {
  const { t } = useTranslation();

  return (
    <Flex direction={'row'}>
      <DescriptionCell
        description={t('common.stock_and_availabilities', 'Stocks and availabilities')}
        rowIndex={rowIndex}
      />
      {selectedReferences?.map((reference, index) => {
        if (!reference) {
          return <EmptyCell key={`${index}_${reference}`} rowIndex={rowIndex} columnIndex={index + 1} />;
        }
        return (
          <TableCell
            key={`${index}_${reference}`}
            align={'flex-start'}
            minWidth={VALUE_CELL_WIDTH}
            rowIndex={rowIndex}
            columnIndex={index + 1}
          >
            <StockDisplay vehicleKey={vehicleKey} referenceNumber={reference} isApplicableToCurrentVehicle />
          </TableCell>
        );
      })}
    </Flex>
  );
};

const PriceRow = ({ selectedReferences, vehicleKey, rowIndex }: RowProps) => {
  const { t } = useTranslation();

  return (
    <Flex direction={'row'}>
      <DescriptionCell description={t('common.price.title', 'Price')} rowIndex={rowIndex} />
      {selectedReferences?.map((reference, index) => (
        <PriceCell
          key={`${index}_${reference}`}
          referenceNumber={reference}
          vehicleKey={vehicleKey}
          rowIndex={rowIndex}
          columnIndex={index + 1}
        />
      ))}
    </Flex>
  );
};

interface PriceCellProps extends TableCellPositionProps {
  referenceNumber?: string;
  vehicleKey?: string;
}

const PriceCell = ({ referenceNumber, vehicleKey, rowIndex, columnIndex }: PriceCellProps) => {
  const reference = getData(useSelector((state: RootState) => getIAMReference(state, { vehicleKey, referenceNumber })));
  const sparePartsView = useSelector(getSparePartsView);

  if (!reference) {
    return <EmptyCell rowIndex={rowIndex} columnIndex={columnIndex} />;
  }
  return (
    <TableCell minWidth={VALUE_CELL_WIDTH} rowIndex={rowIndex} columnIndex={columnIndex}>
      {reference && (
        <ReferencePriceSection
          referenceNumber={reference.referenceNumber}
          handleAddToCartClick={() => {
            return;
          }}
          vehicleKey={undefined}
          sparePartsView={sparePartsView}
          catalogSource={IAM}
          referenceType={STANDARD}
          hideButtons
        />
      )}
    </TableCell>
  );
};

const QuantityPerUnitRow = ({ selectedReferences, vehicleKey, rowIndex }: RowProps) => {
  const { t } = useTranslation();

  return (
    <Flex direction={'row'}>
      <DescriptionCell
        description={t('catalog.details.additional_information.quantity_per_unit', 'Quantity per unit')}
        rowIndex={rowIndex}
      />
      {selectedReferences?.map((reference, index) => (
        <QuantityPerUnitCell
          key={`${index}_${reference}`}
          vehicleKey={vehicleKey}
          referenceNumber={reference}
          rowIndex={rowIndex}
          columnIndex={index + 1}
        />
      ))}
    </Flex>
  );
};

interface QuantityPerUnitCellProps extends TableCellPositionProps {
  referenceNumber?: string;
  vehicleKey?: string;
}

const QuantityPerUnitCell = ({ referenceNumber, vehicleKey, rowIndex, columnIndex }: QuantityPerUnitCellProps) => {
  const reference = getData(useSelector((state: RootState) => getIAMReference(state, { vehicleKey, referenceNumber })));

  if (!reference) {
    return <EmptyCell rowIndex={rowIndex} columnIndex={columnIndex} />;
  }
  return (
    <TableCell minWidth={VALUE_CELL_WIDTH} rowIndex={rowIndex} columnIndex={columnIndex}>
      {reference && (
        <Text type="base" color="grey35">
          {(reference?.quantityPerUnit ?? 0) === 0 ? '-' : reference?.quantityPerUnit}
        </Text>
      )}
    </TableCell>
  );
};

interface AdditionalInfoRowProps extends TableCellRowPositionProps {
  selectedReferences: string[];
  vehicleKey?: string;
  additionalInformation?: AdditionalInformation;
}

const AdditionalInfoRow = ({
  selectedReferences,
  vehicleKey,
  additionalInformation,
  rowIndex,
}: AdditionalInfoRowProps) => {
  return (
    <Flex direction={'row'}>
      <DescriptionCell description={additionalInformation?.title} rowIndex={rowIndex} />
      {selectedReferences.map((reference, index) => (
        <AdditionalInfoCell
          key={`${index}_${reference}`}
          referenceNumber={reference}
          vehicleKey={vehicleKey}
          mainAdditionalInfo={additionalInformation}
          rowIndex={rowIndex}
          columnIndex={index + 1}
        />
      ))}
    </Flex>
  );
};

interface AdditionalInfoCellProps extends TableCellPositionProps {
  referenceNumber?: string;
  vehicleKey?: string;
  mainAdditionalInfo?: AdditionalInformation;
}

const AdditionalInfoCell = ({
  referenceNumber,
  vehicleKey,
  mainAdditionalInfo,
  rowIndex,
  columnIndex,
}: AdditionalInfoCellProps) => {
  const reference = getData(useSelector((state: RootState) => getIAMReference(state, { vehicleKey, referenceNumber })));

  const currentAdditionalInfo = reference?.additionalInformation?.find(
    (parameter) => parameter.id === mainAdditionalInfo?.id,
  );

  const isParameterEqualWithMainReference = mainAdditionalInfo?.description === currentAdditionalInfo?.description;

  if (!reference || !currentAdditionalInfo?.description) {
    return <EmptyCell rowIndex={rowIndex} columnIndex={columnIndex} />;
  }

  return (
    <TableCell minWidth={VALUE_CELL_WIDTH} rowIndex={rowIndex} columnIndex={columnIndex}>
      <Text type="base" color={isParameterEqualWithMainReference ? 'grey35' : 'clear_blue'}>
        {columnIndex === 1 ? mainAdditionalInfo?.description : currentAdditionalInfo?.description}
      </Text>
    </TableCell>
  );
};

interface CompareTableProps {
  selectedReferences: CompareTableState;
  vehicleKey?: string;
  compareReferencesList?: string[];
  handleSelect?: (referenceNumber: string, columnIndex?: number) => void;
}

export const CompareTable = ({
  selectedReferences,
  vehicleKey,
  compareReferencesList,
  handleSelect,
}: CompareTableProps) => {
  const mainReference = getData(
    useSelector((state: RootState) =>
      getIAMReference(state, { vehicleKey, referenceNumber: selectedReferences.firstReference }),
    ),
  );
  const secondReference = getData(
    useSelector((state: RootState) =>
      getIAMReference(state, { vehicleKey, referenceNumber: selectedReferences.secondReference }),
    ),
  );
  const thirdReference = getData(
    useSelector((state: RootState) =>
      getIAMReference(state, { vehicleKey, referenceNumber: selectedReferences.thirdReference }),
    ),
  );

  const selectedReferencesArray = [
    selectedReferences.firstReference,
    selectedReferences.secondReference,
    selectedReferences.thirdReference,
  ];
  const firstAdditionalInfos = mainReference?.additionalInformation ?? [];
  const secondAdditionalInfos = [...(secondReference?.additionalInformation ?? [])].filter(
    (r) => firstAdditionalInfos.findIndex((item) => item.id === r.id) === -1,
  );
  const thirdAdditionalInfos = [...(thirdReference?.additionalInformation ?? [])].filter(
    (r) => [...firstAdditionalInfos, ...secondAdditionalInfos].findIndex((item) => item.id === r.id) === -1,
  );

  return (
    <TableWrapper>
      <Flex direction={'column'} minHeight={76}>
        <SummaryRow
          selectedReferences={selectedReferencesArray}
          vehicleKey={vehicleKey}
          compareReferencesList={compareReferencesList}
          handleSelect={handleSelect}
          rowIndex={0}
        />
        <StocksRow selectedReferences={selectedReferencesArray} vehicleKey={vehicleKey} rowIndex={1} />
        <PriceRow selectedReferences={selectedReferencesArray} vehicleKey={vehicleKey} rowIndex={2} />
        <QuantityPerUnitRow selectedReferences={selectedReferencesArray} vehicleKey={vehicleKey} rowIndex={3} />
        {[...firstAdditionalInfos, ...secondAdditionalInfos, ...thirdAdditionalInfos].map(
          (additionalInformationParameter, index) => {
            return (
              <AdditionalInfoRow
                key={additionalInformationParameter.id}
                selectedReferences={selectedReferencesArray}
                vehicleKey={vehicleKey}
                additionalInformation={additionalInformationParameter}
                rowIndex={index + 4}
              />
            );
          },
        )}
      </Flex>
    </TableWrapper>
  );
};
